import { useMemo } from 'react';
import { CIRCULAR_TXN_KEY, NET_KEY, TOTAL_COLUMN_KEY, formatINR, isSummaryRow, parseINR } from './components/overlap.constants';
import { SORT_DIRECTIONS, SortConfig } from '../../../../../utils/constants/commonConstants';

export interface RevenueTableData {
    fetchSuccess: boolean;
    headers: { name: string; key: string }[];
    rows: any[];
    totalRecords: number;
    records: number;
    // Per-month intra-company share (%), keyed identically to the month column keys.
    intraCompanyMonthly?: Record<string, number>;
    // Per-month intra-company revenue in ₹ (same keys) — used to compute Net B2B Revenue (Total − Intra).
    intraCompanyMonthlyAmount?: Record<string, number>;
}

export interface RevenueTableView {
    displayHeaders: { name: string; key: string }[];
    displayRows: any[];
}

/**
 * Pure derivation of the B2B revenue table's display headers + rows from the raw response.
 * Extracted from the component and exported so the money math (Net B2B Revenue = Total − Intra,
 * the injected Circular/Net columns, and the Total-column sort) is unit-testable without React.
 */
export function buildRevenueTableView(
    tableData: RevenueTableData | undefined,
    isRevenue: boolean,
    sortConfig?: SortConfig,
): RevenueTableView {
    // B2B revenue table gets two extra pinned-right columns — Circular Txn. (% badge) and Net
    // (= Total − Circular) — injected client-side. Vendor Expense keeps the original column set.
    const displayHeaders =
        isRevenue && tableData?.headers?.length
            ? [...tableData.headers, { name: 'Circular Txn.', key: CIRCULAR_TXN_KEY }, { name: 'Net', key: NET_KEY }]
            : tableData?.headers || [];

    const displayRows = (() => {
        let rows = tableData?.rows || [];
        if (!isRevenue || !rows.length) return rows;

        // Relabel the backend "Total Revenue" summary row to "Total B2B Revenue" and append a
        // derived "Net B2B Revenue" row: Net = Total − Intra Company Txn., per month and total
        // (per product — "Net B2B revenue after subtracting Intra Company Txn.").
        const totalRow = rows.find((r) => r?.name === 'Total Revenue');
        rows = rows.map((r) => (r?.name === 'Total Revenue' ? { ...r, name: 'Total B2B Revenue' } : r));

        // Intra Company Txn. row — per-month intra share % (backend `intraCompanyMonthly`). Carries
        // the Total B2B row (per-month ₹) and the intra ₹ amounts so each month's badge can render a
        // rich Total / Intra / Net tooltip, mirroring Circular Txn.
        const intra = tableData?.intraCompanyMonthly;
        const intraRow =
            intra && Object.keys(intra).length
                ? {
                      name: 'Intra Company Txn.',
                      __isIntra: true,
                      __totalRow: totalRow,
                      __amounts: tableData?.intraCompanyMonthlyAmount || {},
                      ...intra,
                  }
                : null;

        if (totalRow) {
            // Exact intra ₹ per month from the backend (`intraCompanyMonthlyAmount`) — subtract from
            // each month's Total, and the overall Total column, to get Net B2B Revenue.
            const intraAmt: Record<string, number> = tableData?.intraCompanyMonthlyAmount || {};
            const totalIntra = Object.values(intraAmt).reduce((sum, v) => sum + (Number(v) || 0), 0);
            const netB2bRow: any = {
                ...totalRow,
                name: 'Net B2B Revenue',
                [TOTAL_COLUMN_KEY]: formatINR(parseINR(totalRow[TOTAL_COLUMN_KEY]) - totalIntra),
                isOverlapping: undefined,
                circularValue: undefined,
                circularTotal: undefined,
                circularPct: undefined,
            };
            // Net per month = that month's Total − that month's intra amount. Months with no intra
            // keep the Total value (net = total).
            Object.keys(intraAmt).forEach((m) => {
                if (totalRow[m] !== undefined) {
                    netB2bRow[m] = formatINR(parseINR(totalRow[m]) - (Number(intraAmt[m]) || 0));
                }
            });
            rows = [...rows, ...(intraRow ? [intraRow] : []), netB2bRow];
        } else if (intraRow) {
            rows = [...rows, intraRow];
        }

        // Sort customer rows by the chosen column (Total or a month); summary rows stay pinned
        // at the bottom in order. Cells with no value ('-'/absent) sort as 0.
        if (!sortConfig?.direction) return rows;
        const field = sortConfig.field || TOTAL_COLUMN_KEY;
        const asc = sortConfig.direction === SORT_DIRECTIONS.ASC;
        const customers = rows.filter((r) => !isSummaryRow(r));
        const summaries = rows.filter((r) => isSummaryRow(r));
        const sorted = [...customers].sort((a, b) => {
            const diff = parseINR(a?.[field]) - parseINR(b?.[field]);
            return asc ? diff : -diff;
        });
        return [...sorted, ...summaries];
    })();

    return { displayHeaders, displayRows };
}

/** Memoised React wrapper around buildRevenueTableView. */
export default function useRevenueTableRows(
    tableData: RevenueTableData | undefined,
    isRevenue: boolean,
    sortConfig?: SortConfig,
): RevenueTableView {
    return useMemo(() => buildRevenueTableView(tableData, isRevenue, sortConfig), [tableData, isRevenue, sortConfig]);
}
